import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSendTransfer } from './useSendTransfer';
import { topupQuoteApi, topupCardApi } from '../lib/cards';
import { ApiError, rpcErrorCode } from '../lib/api';
import { TransferSettlementError, waitForTransferSettlement } from '../lib/transfers';

export type TopUpStage = 'idle' | 'quoting' | 'paying' | 'confirming' | 'loading';

function randomKey(): string {
  const g = globalThis.crypto;
  if (g?.randomUUID) return g.randomUUID();
  return `k${Date.now()}${Math.random().toString(16).slice(2)}`;
}

/**
 * Top-up: quote → pay (or resume durable paymentTransferId) → short wait →
 * cards.topup. Server also settles in the background after confirm, so a dropped
 * client still gets Banana recharge without a second send.
 */
export function useTopUpCard() {
  const qc = useQueryClient();
  const { send } = useSendTransfer();
  const [stage, setStage] = useState<TopUpStage>('idle');
  const activeRef = useRef<Promise<void> | null>(null);
  const paidRef = useRef<{ cardId: string; amount: string; transferId: string } | null>(null);

  function topUp(params: {
    cardId: string;
    amount: string;
    walletId: string;
    index: number;
    address: string;
    reauthToken?: string;
  }): Promise<void> {
    if (activeRef.current) return activeRef.current;
    const task = runTopUp(params).finally(() => {
      activeRef.current = null;
    });
    activeRef.current = task;
    return task;
  }

  async function runTopUp(params: {
    cardId: string;
    amount: string;
    walletId: string;
    index: number;
    address: string;
    reauthToken?: string;
  }): Promise<void> {
    const { cardId, walletId, index, address, reauthToken } = params;
    let amount = params.amount;
    try {
      let paid = paidRef.current;
      if (paid && paid.cardId !== cardId) {
        paid = null;
        paidRef.current = null;
      }

      if (!paid) {
        setStage('quoting');
        const quote = await topupQuoteApi(cardId, amount);

        if (quote.resumeTransferId) {
          // Server owns the payment amount on resume (orphan / stuck intent).
          amount = String(quote.amountUsd);
          paid = { cardId, amount, transferId: quote.resumeTransferId };
          paidRef.current = paid;
        } else if (quote.paymentRequired) {
          setStage('paying');
          const { transferId } = await send({
            walletId,
            index,
            address,
            to: quote.payTo,
            amount: quote.totalUsd.toFixed(2),
            coin: 'USDT',
            reauthToken,
            cardPayment: { kind: 'topup', cardId, amount: Number(amount) },
          });
          if (!transferId) throw new ApiError(502, 'payment_failed');
          paid = { cardId, amount, transferId };
          paidRef.current = paid;
        }
      }

      if (paid) {
        setStage('confirming');
        try {
          // Short wait — server sweeper settles if we drop; don't block 75s.
          await waitForTransferSettlement(paid.transferId, 25_000);
        } catch (error) {
          if (error instanceof TransferSettlementError) {
            paidRef.current = null;
            throw error;
          }
          // payment_not_confirmed / timeout: still try topup — server may have
          // confirmed + settled, or topup will return payment_not_confirmed.
          if (rpcErrorCode(error) !== 'payment_not_confirmed') throw error;
        }
      }

      setStage('loading');
      await topupCardApi({
        id: cardId,
        amount,
        ...(paid ? { paymentTransferId: paid.transferId } : {}),
        idempotencyKey: paid ? `topup-${paid.transferId}` : randomKey(),
        reauthToken,
      });

      paidRef.current = null;
      void qc.invalidateQueries({ queryKey: ['cards'] });
      void qc.invalidateQueries({ queryKey: ['cardTx', cardId] });
      setStage('idle');
    } catch (e) {
      // Keep paidRef on provider/network failures and topup_payment_pending so
      // Retry reuses the same paymentTransferId. Clear only terminal payment loss.
      const code = rpcErrorCode(e);
      if (code === 'payment_already_used' || e instanceof TransferSettlementError) {
        paidRef.current = null;
      }
      setStage('idle');
      throw e;
    }
  }

  return { stage, topUp };
}
